import { useParams } from 'react-router';
import { Collapse, Card, CardHeader, CardBody, Col, Row, Table } from 'reactstrap';
import { getReducer, getSetStateFunction, getAPICallGenerator, callBackGenerator, formatNumber, getStateCode } from '../../util/util';
import React, { useReducer, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import MyHeatMapDrillDown from '../util/my-heat-map-drilldown';
import MyReactTable from '../util/my-react-table';

const initialState = {
  companyName:'@Home Statistic Report',
  report: {
    total_order: 0,
    avg_distance: -1,
    highest_tat_bd: -1,
    avg_tat_bd: -1,
    lowest_tat_bd: -1,
    total_appraiser_used: 0,
    avg_appraiser_used: 0,
    highest_appraiser_used: 0,
    lowest_appraiser_used: 0,
    condition_rate: 0,
    no_condition: 0,
    condition_more_than_three: 0,
    avg_condition: 0,
    rebuttal_rate: 0,
    no_rebuttal: 0,
    rebuttal_more_than_three: 0,
    avg_rebuttal: 0,
    appraisal_types: [],
    states: [],
    counties: []
  },
  showMap: true,
  heatMapDrillDownStateCounty: '',
  heatMapDrillDownLevel: ''
};

const reducer = getReducer();

const ClientStatistic = (props) => {
  const controller = new AbortController();

  let paramEncoded = useParams().params;
  let param = atob(paramEncoded);
  
  try {
    param = atob(paramEncoded);
  } catch (e) {
    console.error('Invalid client statistic public report link:', paramEncoded, e);
  }


  const [state, dispatch] = useReducer(reducer, initialState);

  const setState = getSetStateFunction(dispatch);
  const apiCallBack = callBackGenerator(setState);
  const httpGet = getAPICallGenerator(props, { signal: controller.signal, noToken: true });

  useEffect(() => {
    getReport();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getParamValue = (key) => {
    let tokens = param.split('&');

    for (let i = 0; i < tokens.length; i++) {
      let tmp = tokens[i].split('=');

      if (tmp.length > 1 && tmp[0] === key) {
        return tmp[1];
      }
    }

    return '';
  };

  const getReport = () => {
    let callBack = apiCallBack([{ state: 'report', key: 'data' },{ state: 'companyName', key: 'company' }]);

    httpGet(
      'report/client/statistic/public/' + paramEncoded,
      '',
      'Oops, something went wrong and could not load the report. Please try again later.',
      callBack
    );
  };

  const heatMapOnClick = (stateCounty, level = 'state') => {
    setState({ heatMapDrillDownLevel: level, heatMapDrillDownStateCounty: stateCounty });
  };

  const isMissing = (value) => {
    return value === undefined || value === null || value === '' || value === -1 || value === '-1';
  };

  const formatValue = (value) => {
    return isMissing(value) ? '-' : formatNumber(value);
  };

  const formatMoney = (value) => {
    return isMissing(value) ? '-' : '$' + formatNumber(value);
  };

  const formatBd = (value) => {
    return isMissing(value) ? '-' : formatNumber(value) + ' bd';
  };

  const formatDay = (value) => {
    return isMissing(value) ? '-' : formatNumber(value) + ' d';
  };

  const from = getParamValue('from');
  const to = getParamValue('to');

  let mapData = [];
  let mapCountyData = [];
  let map;
  let joinBy = ['postal-code', 'code'];

  for (let i = 0; i < state.report.states.length; i++) {
    mapData.push({
      state: state.report.states[i].state,
      state_short: getStateCode(state.report.states[i].state),
      code: getStateCode(state.report.states[i].state),
      value: state.report.states[i].tat_bd,
      overall_total: state.report.states[i].total_order,
      total_complex: state.report.states[i].total_complex
    });
  }

  for (let i = 0; i < state.report.counties.length; i++) {
    mapCountyData.push({
      state: state.report.counties[i].state,
      state_short: getStateCode(state.report.counties[i].state),
      county: state.report.counties[i].county.trim(),
      value: state.report.counties[i].tat_bd,
      overall_total: state.report.counties[i].total_order,
      total_complex: state.report.counties[i].total_complex
    });
  }

  if (mapData.length > 0) {
    map = <MyHeatMapDrillDown
      data={mapData}
      data2={mapCountyData}
      legendTitle="day"
      joinBy={joinBy}
      title="Turnaround Time (days)"
      onClick={heatMapOnClick}
    />;
  }

  let productColumns = [
    {
      id: 'product',
      Header: 'Product',
      minWidth: 350,
      accessor: d => d.external_label,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'totalOrder',
      Header: 'Total Order',
      accessor: d => d.total_order,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'fee',
      Header: 'Avg Fee',
      accessor: d => d.fee,
      Cell: props => <div>{formatMoney(props.row.original.fee)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'tatBD',
      Header: 'Turnaround Business Days',
      accessor: d => d.tat_bd,
      Cell: props => <div>{formatBd(props.row.original.tat_bd)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'unclassified',
      Header: 'Unclassified',
      accessor: d => d.unknown_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.unknown_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'urban',
      Header: 'Urban',
      accessor: d => d.urban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.urban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'suburban',
      Header: 'Suburban',
      accessor: d => d.suburban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.suburban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'rural',
      Header: 'Rural',
      accessor: d => d.rural_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.rural_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'complex',
      Header: 'Complex',
      accessor: d => d.total_complex,
      headerStyle: { textAlign: 'left' }
    }
  ];

  let stateColumns = [
    {
      id: 'state',
      Header: 'State',
      minWidth: 250,
      accessor: d => d.state,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'totalOrder',
      Header: 'Total Order',
      accessor: d => d.total_order,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'fee',
      Header: 'Avg Fee',
      accessor: d => d.fee,
      Cell: props => <div>{formatMoney(props.row.original.fee)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'tatBD',
      Header: 'Turnaround Business Days',
      accessor: d => d.tat_bd,
      Cell: props => <div>{formatBd(props.row.original.tat_bd)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'unclassified',
      Header: 'Unclassified',
      accessor: d => d.unknown_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.unknown_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'urban',
      Header: 'Urban',
      accessor: d => d.urban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.urban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'suburban',
      Header: 'Suburban',
      accessor: d => d.suburban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.suburban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'rural',
      Header: 'Rural',
      accessor: d => d.rural_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.rural_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'complex',
      Header: 'Complex',
      accessor: d => d.total_complex,
      headerStyle: { textAlign: 'left' }
    }
  ];

  let countyColumns = [
    {
      id: 'state',
      Header: 'State',
      width: 120,
      accessor: d => d.state,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'county',
      Header: 'County',
      minWidth: 250,
      accessor: d => d.county,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'totalOrder',
      Header: 'Total Order',
      accessor: d => d.total_order,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'fee',
      Header: 'Avg Fee',
      accessor: d => d.fee,
      Cell: props => <div>{formatMoney(props.row.original.fee)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'tatBD',
      Header: 'Turnaround Business Days',
      accessor: d => d.tat_bd,
      Cell: props => <div>{formatBd(props.row.original.tat_bd)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'unclassified',
      Header: 'Unclassified',
      accessor: d => d.unknown_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.unknown_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'urban',
      Header: 'Urban',
      accessor: d => d.urban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.urban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'suburban',
      Header: 'Suburban',
      accessor: d => d.suburban_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.suburban_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'rural',
      Header: 'Rural',
      accessor: d => d.rural_avg_turntime,
      Cell: props => <div>{formatBd(props.row.original.rural_avg_turntime)}</div>,
      headerStyle: { textAlign: 'left' }
    },
    {
      id: 'complex',
      Header: 'Complex',
      accessor: d => d.total_complex,
      headerStyle: { textAlign: 'left' }
    }
  ];

  return (
    <div>
      <div className="topbar">
        <div className="topbar-logo-container">
          <NavLink to="/">
            <img className="topbar-logo-img" alt="@Home VMS Logo" width="120px" src="/img/logo_small.png"/>
          </NavLink>
        </div>
      </div>

      <div className="padding">
        <Card>
          <CardHeader className="header-color">
            <i className="fa fa-reorder"></i>&nbsp;{state.companyName}
          </CardHeader>
          <CardBody>
            <center>
              <label>{from} - {to}</label>
            </center>

            <br/>

            <Row>
              <Col sm="3">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Order Volume</CardHeader>
                  <CardBody style={{ height: '200px' }}>
                    <center>
                      <br/>
                      <br/>
                      <div style={{ fontSize: '35px' }}>{state.report.total_order}</div>
                      <font style={{ fontSize: '12px' }}>orders</font>
                    </center>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="3">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Avg Vendor Distance</CardHeader>
                  <CardBody style={{ height: '200px' }}>
                    <center>
                      <br/>
                      <br/>
                      <div style={{ fontSize: '35px' }}>{formatValue(state.report.avg_distance)}</div>
                      <font style={{ fontSize: '12px' }}>miles</font>
                    </center>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="6">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Turnaround Time</CardHeader>
                  <CardBody style={{ height: '200px' }}>
                    <Table>
                      <tbody>
                        <tr>
                          <td></td>
                          <td style={{ fontWeight: 'bold' }}>Business Days</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 'bold' }}>Highest</td>
                          <td>{state.report.highest_tat_bd>0?formatValue(state.report.highest_tat_bd):'less than 24 hours'}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 'bold' }}>Average</td>
                          <td>{state.report.avg_tat_bd>0?formatValue(state.report.avg_tat_bd):'less than 24 hours'}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 'bold' }}>Lowest</td>
                          <td>{state.report.lowest_tat_bd>0?formatValue(state.report.lowest_tat_bd):'less than 24 hours'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <br/>

            <Row>
              <Col sm="4">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Vendor Turnover</CardHeader>
                  <CardBody style={{ height: '100px' }}>
                    <Row>
                      <Col sm="4">
                        <center>
                          <div style={{ fontSize: '35px' }}>{state.report.total_appraiser_used}</div>
                          <font style={{ fontSize: '12px' }}>vendors used</font>
                        </center>
                      </Col>
                      <Col sm="8" style={{ fontSize: '13px' }}>
                        <Row>
                          <Col sm="8">- Avg order per vendor</Col>
                          <Col sm="4">{formatValue(state.report.avg_appraiser_used)}</Col>
                        </Row>
                        <Row>
                          <Col sm="8">- Highest order per vendor</Col>
                          <Col sm="4">{formatValue(state.report.highest_appraiser_used)}</Col>
                        </Row>
                        <Row>
                          <Col sm="8">- Lowest order per vendor</Col>
                          <Col sm="4">{formatValue(state.report.lowest_appraiser_used)}</Col>
                        </Row>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="4">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Condition</CardHeader>
                  <CardBody style={{ height: '100px' }}>
                    <Row>
                      <Col sm="4">
                        <center>
                          <div style={{ fontSize: '35px' }}>{formatValue(Number(state.report.condition_rate) * 100)}</div>
                          <font style={{ fontSize: '12px' }}>%</font>
                        </center>
                      </Col>
                      <Col sm="8" style={{ fontSize: '13px' }}>
                        <Row>
                          <Col sm="8">- Report without revision</Col>
                          <Col sm="4">{state.report.no_condition}</Col>
                        </Row>
                        <Row>
                          <Col sm="8">- 3 or more revision</Col>
                          <Col sm="4">{formatValue(state.report.condition_more_than_three)}</Col>
                        </Row>
                        <Row>
                          <Col sm="8">- Avg revision per condition order</Col>
                          <Col sm="4">{formatValue(state.report.avg_condition)}</Col>
                        </Row>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="4">
                <Card style={{border: '1px solid #d2d2d2', borderRadius: '5px', overflow: 'hidden'}}>
                  <CardHeader>Rebuttal</CardHeader>
                  <CardBody style={{ height: '100px' }}>
                    <Row>
                      <Col sm="4">
                        <center>
                          <div style={{ fontSize: '35px' }}>{formatValue(Number(state.report.rebuttal_rate) * 100)}</div>
                          <font style={{ fontSize: '12px' }}>%</font>
                        </center>
                      </Col>
                      <Col sm="8" style={{ fontSize: '13px' }}>
                        <Row>
                          <Col sm="8">- Report without rebuttal</Col>
                          <Col sm="4">{state.report.no_rebuttal}</Col>
                        </Row>
                        {/* <Row>
                          <Col sm="8">- 3 or more rebuttal</Col>
                          <Col sm="4">{formatValue(state.report.rebuttal_more_than_three)}</Col>
                        </Row> */}
                        <Row>
                          <Col sm="8">- Avg rebuttal per rebuttal order</Col>
                          <Col sm="4">{formatValue(state.report.avg_rebuttal)}</Col>
                        </Row>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <br/>
            {map}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ClientStatistic;